import React from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import Button from "@components/Button";
import Input from "@components/Input";
import SizedBox from "@components/SizedBox";
import { ROUTES } from "@stores/AccountStore";
import { observer } from "mobx-react-lite";
import { useStores } from "@stores";
import { useLang } from "@src/hooks/useLang";
import AuthLayout, { ErrorAlert } from "@components/AuthLayout";
import { authClient } from "@services/authService";
import Text from "@components/Text";
import { savePendingInvite } from "@src/utils/pendingInvite";

const RegisterScreen: React.FC = () => {

  const lang = useLang();
  const navigate = useNavigate();
  const { accountStore } = useStores();
  const [searchParams] = useSearchParams();
  // Team invitations link here with the email pre-filled (?email=...&invite=...).
  // The token is kept until the user has verified the address and signed in;
  // AccountStore.auth() then redeems it, so access is granted by the token and
  // not by the email match alone (audit SEC-3).
  const invitedEmail = searchParams.get("email") ?? "";
  const inviteToken = searchParams.get("invite");
  const isInvite = !!inviteToken;
  React.useEffect(() => {
    if (inviteToken) savePendingInvite(inviteToken);
  }, [inviteToken]);
  const t = (lang as any).team ?? {};

  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState(invitedEmail);
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  if (accountStore.authenticated) {
    return <Navigate to={ROUTES.FIELDS} />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error } = await authClient.signUp.email({ name, email, password });
      if (error) {
        setError(error.message || "Registration failed");
        setLoading(false);
        return;
      }
      // Signup no longer creates a session (requireEmailVerification) — a
      // 6-digit code was emailed; the verify screen signs the user in.
      navigate(ROUTES.VERIFY_EMAIL, { state: { email } });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed");
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit} style={{ width: 336 }}>
        <ErrorAlert enabled={!!error}>{error}</ErrorAlert>
        {isInvite && (
          <>
            <Text size={14} color="#68AD18" style={{ textAlign: "center", width: "100%" }}>
              {t.inviteBanner ?? "You've been invited. Create your account to accept."}
            </Text>
            <SizedBox height={16} />
          </>
        )}
        <Input
          label={lang.profile.nameTitle}
          placeholder={lang.profile.nameFill}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <SizedBox height={16} />
        <Input
          label={lang.auth.emailTitle}
          placeholder={lang.auth.emailField}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <SizedBox height={16} />
        <Input
          label={lang.auth.passwordTitle}
          placeholder={lang.auth.passwordField}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <SizedBox height={24} />
        <Button type="submit" disabled={loading}>
          {lang.auth.registerButton}
        </Button>
        <SizedBox height={16} />
        <Text
          size={14}
          color="#68AD18"
          style={{ cursor: "pointer", textAlign: "center", width: "100%" }}
          onClick={() => navigate(ROUTES.LOGIN)}
        >
          {lang.auth.signInButton}
        </Text>
      </form>
    </AuthLayout>
  );
};

export default observer(RegisterScreen);
